import { useForm } from 'react-hook-form';

import type { ContentRefOpenAPI } from '@gitbook/api-client';
import { type BlockOpenAPIData, openapiOperation } from '@gitbook/doc-core';
import type { BlockProps, EditorContainer } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Description,
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    DialogRoot,
    DialogTrigger,
    Form,
    Heading,
    Submit,
} from '@gitbook/spine-react';
import { assert } from '@gitbook/util-ts';

import { useCurrentOrganizationId } from '../../../state';
import { useContentReference } from '../../ContentReferences';
import { OpenAPIComputedIncentiveAlert, OpenAPISpecPicker } from '../../OpenAPI';
import { useIsBlockHovered } from '../common/layout/hovered';
import { ReferenceBlockActions, ReferenceBlockFrame } from '../ReferenceBlockFrame';
import { openapiFetcherCache } from './cache';
import { OpenAPIBlockWrapper } from './OpenAPIBlockWrapper';
import { OpenAPIOperationBlockContent } from './OpenAPIOperation';

/**
 * Component to render a swagger block.
 */
export function BlockOpenAPI(props: BlockProps) {
    const data = props.node.data.toFrozenObject<BlockOpenAPIData>();
    return (
        <OpenAPIBlockWrapper {...props}>
            <OpenAPIFetchOperation
                data={data}
                isFocused={props.isFocused}
                blockKey={props.node.key}
                editor={props.editor}
            />
        </OpenAPIBlockWrapper>
    );
}

/**
 * Fetch the operation (will suspense).
 */
function OpenAPIFetchOperation(props: {
    data: BlockOpenAPIData;
    isFocused: boolean;
    blockKey: string;
    editor: EditorContainer;
}) {
    const {
        data: { ref: contentRef, path, method },
        isFocused,
        blockKey,
        editor,
    } = props;
    const resolved = useContentReference(contentRef);
    const specUrl = resolved.href;
    const filesystem = openapiFetcherCache.read(specUrl);

    return (
        <>
            {!editor.readOnly && (
                <BlockOpenAPIFrame
                    isFocused={isFocused}
                    blockKey={blockKey}
                    editor={editor}
                    path={path}
                    method={method}
                />
            )}
            <OpenAPIOperationBlockContent
                specUrl={specUrl}
                filesystem={filesystem}
                path={path}
                method={method}
            />
        </>
    );
}

function BlockOpenAPIFrame(props: {
    isFocused: boolean;
    blockKey: string;
    editor: EditorContainer;
    path: string;
    method: string;
}) {
    const { isFocused, blockKey, editor, path, method } = props;
    const isHovered = useIsBlockHovered(blockKey);
    return (
        <ReferenceBlockFrame isFocused={isFocused} isHovered={isHovered}>
            <ReferenceBlockActions>
                <DialogRoot>
                    <DialogTrigger>
                        <Button
                            leadingIcon={Icon.Warning}
                            size="small"
                            kind="warning"
                            label="Migrate"
                        />
                    </DialogTrigger>
                    <MigrateDialog
                        onMigrate={(sourceRef) => {
                            editor.change((change) => {
                                change.replaceNodeByKey(
                                    blockKey,
                                    openapiOperation.createBlockWithRef({
                                        ref: sourceRef,
                                        path,
                                        method,
                                    })
                                );
                            });
                        }}
                    />
                </DialogRoot>
            </ReferenceBlockActions>
        </ReferenceBlockFrame>
    );
}

interface MigrateFormProps {
    onMigrate: (sourceRef: ContentRefOpenAPI) => void;
}

function MigrateDialog(props: MigrateFormProps) {
    return (
        <Dialog>
            <DialogHeader>
                <Heading>Migrate OpenAPI operation block</Heading>
                <Description>
                    To migrate this block to the new OpenAPI format, please select the OpenAPI
                    specification you want to link to.
                </Description>
            </DialogHeader>
            <MigrateForm onMigrate={props.onMigrate} />
        </Dialog>
    );
}

type MigrateFormValues = {
    specSlug: string | null;
};

function MigrateForm(props: MigrateFormProps) {
    const { onMigrate } = props;
    const organizationId = useCurrentOrganizationId();
    assert(organizationId, 'Organization ID is required');
    const form = useForm<MigrateFormValues>({
        defaultValues: {
            specSlug: null,
        },
    });

    const onSubmit = form.handleSubmit((data) => {
        assert(data.specSlug, 'specSlug is required');
        onMigrate({ kind: 'openapi', spec: data.specSlug });
    });

    const specSlug = form.watch('specSlug');

    return (
        <Form form={form} onSubmit={onSubmit}>
            <DialogBody>
                <OpenAPISpecPicker
                    organizationId={organizationId}
                    form={form}
                    name="specSlug"
                    analyticsContext={{
                        triggerLocation: 'migrate-legacy-block-dialog',
                    }}
                />
                {specSlug && <OpenAPIComputedIncentiveAlert className="mt-4" />}
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
                <Submit label="Migrate" />
            </DialogFooter>
        </Form>
    );
}
